import { useEffect, useMemo, useRef, useState } from "react";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import pickJevCustomerSuggestions from "~/helpers/search/pickJevCustomerSuggestions";
import useDebounce from "~/hooks/useDebounce";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import { type CustomerSearchMatch, suggestCustomerSearchMatches } from "~/services/aiOrchestrator";

const FEW_BUILT_IN_RESULTS = 3;
const PAUSE_MS = 500;

interface UseJevCustomerSuggestionsArgs {
	search: string;
	shownCustomerIds: readonly string[];
	skip: boolean;
}

interface JevAnswer {
	matches: CustomerSearchMatch[];
	text: string;
}

const useJevCustomerSuggestions = ({
	search,
	shownCustomerIds,
	skip
}: UseJevCustomerSuggestionsArgs): CustomerSearchMatch[] => {
	const { isEnabled } = useFeatureFlag(FeatureFlagName.JevDecisions);
	const text = search.trim();
	const pausedText = useDebounce(text, PAUSE_MS);
	const askedTextRef = useRef("");
	const [answer, setAnswer] = useState<JevAnswer | null>(null);
	const canSuggest = isEnabled && shownCustomerIds.length < FEW_BUILT_IN_RESULTS;
	const shouldAsk = canSuggest && !skip && text !== "" && pausedText === text;

	useEffect(() => {
		if (!shouldAsk || askedTextRef.current === text) return;
		askedTextRef.current = text;
		void suggestCustomerSearchMatches(text).then((matches) => {
			if (askedTextRef.current === text) setAnswer({ matches, text });
		});
	}, [shouldAsk, text]);

	return useMemo(
		() => (canSuggest && answer?.text === text ? pickJevCustomerSuggestions(answer.matches, shownCustomerIds) : []),
		[answer, canSuggest, shownCustomerIds, text]
	);
};

export default useJevCustomerSuggestions;
